import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Stage, Layer, Rect, Line, Circle, Text, Group, Image as KonvaImage } from 'react-konva';
import { Maximize2, ZoomIn, ZoomOut, Grid3x3, Ruler, Eye, Code2, Image as ImageIcon } from 'lucide-react';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const LAYER_COLORS: Record<string, string> = {
  'F.Cu': '#ff3333', 'B.Cu': '#3333ff', 'F.SilkS': '#f0f000', 'B.SilkS': '#e040e0',
  'F.Fab': '#c8c800', 'B.Fab': '#5050ff', 'F.CrtYd': '#a0a0ff', 'B.CrtYd': '#ffa0ff',
  'F.Mask': '#ff60ff', 'B.Mask': '#30d050', 'F.Paste': '#ff8080', 'B.Paste': '#8080ff',
  'Dwgs.User': '#c8c8c8', 'Edge.Cuts': '#f0f000',
};
const PCB_BG = '#1a472a';
const PAD_COLOR = '#cc4444';
const PAD_SELECTED_COLOR = '#ff8800';
const PIN1_COLOR = '#ffffff';
const GRID_COLOR = 'rgba(255,255,255,0.08)';
const AXIS_COLOR = 'rgba(255,255,255,0.2)';
const DIM_COLOR = '#00ccff';
const RULER_COLOR = '#ff9900';
const SELECT_STROKE = '#00ff88';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface FootprintPad { id: string; shape: string; x: number; y: number; width: number; height: number; }
interface LayerItem { type: string; x1?: number; y1?: number; x2?: number; y2?: number; width?: number; fill?: boolean; cx?: number; cy?: number; r?: number; points?: number[]; }
interface FootprintLayer { items: LayerItem[]; }
interface Silkscreen { outline?: number[][]; pin1Marker?: { type: string; x: number; y: number }; }
interface FootprintData { package?: string; type?: string; pads?: FootprintPad[]; layers?: Record<string, FootprintLayer>; silkscreen?: Silkscreen; }
interface Dimensions { bodyWidth?: number; bodyHeight?: number; padToPad?: number; unit?: string; }

type SelectionId = string | null; // "pad:0", "layer:F.SilkS:0", "dim", null
type ViewTab = 'layout' | 'json';

interface FootprintPreviewProps {
  footprint: FootprintData | null;
  dimensions?: Dimensions | null;
  croppedPngBase64?: string;
  rawJson?: Record<string, unknown> | null;
  height?: number;
  editable?: boolean;
  onFootprintChange?: (fp: FootprintData) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function calcBounds(fp: FootprintData) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const expand = (x: number, y: number) => { if (x < minX) minX = x; if (y < minY) minY = y; if (x > maxX) maxX = x; if (y > maxY) maxY = y; };
  fp.pads?.forEach(p => { expand(p.x - p.width / 2, p.y - p.height / 2); expand(p.x + p.width / 2, p.y + p.height / 2); });
  if (fp.layers) for (const items of Object.values(fp.layers)) items.items?.forEach(item => {
    if (item.type === 'rect' && item.x1 != null) { expand(item.x1, item.y1!); expand(item.x2!, item.y2!); }
    if (item.type === 'circle' && item.cx != null) { expand(item.cx - (item.r || 0), item.cy! - (item.r || 0)); expand(item.cx + (item.r || 0), item.cy! + (item.r || 0)); }
  });
  fp.silkscreen?.outline?.forEach(([x, y]) => expand(x, y));
  if (!isFinite(minX)) { minX = -5; minY = -5; maxX = 5; maxY = 5; }
  return { minX, minY, maxX, maxY };
}

function useLoadImage(base64?: string) {
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  useEffect(() => {
    if (!base64) { setImg(null); return; }
    const i = new window.Image();
    i.onload = () => setImg(i);
    i.onerror = () => { console.warn('[FootprintPreview] Failed to load cropped PNG'); setImg(null); };
    i.src = `data:image/png;base64,${base64}`;
  }, [base64]);
  return img;
}

// ---------------------------------------------------------------------------
// Canvas sub-components
// ---------------------------------------------------------------------------
const GridLayer: React.FC<{ stagePos: { x: number; y: number; scale: number }; w: number; h: number; mm: number; spacing: number }> = ({ stagePos, w, h, mm, spacing }) => {
  const lines: React.ReactNode[] = [];
  const vMinX = (-stagePos.x / stagePos.scale) / mm, vMinY = (-stagePos.y / stagePos.scale) / mm;
  const vMaxX = ((w - stagePos.x) / stagePos.scale) / mm, vMaxY = ((h - stagePos.y) / stagePos.scale) / mm;
  const sX = Math.floor(vMinX / spacing) * spacing, eX = Math.ceil(vMaxX / spacing) * spacing;
  const sY = Math.floor(vMinY / spacing) * spacing, eY = Math.ceil(vMaxY / spacing) * spacing;
  for (let x = sX; x <= eX; x += spacing) { const ax = Math.abs(x) < 0.001; lines.push(<Line key={`gv${x}`} points={[x * mm, sY * mm, x * mm, eY * mm]} stroke={ax ? AXIS_COLOR : GRID_COLOR} strokeWidth={ax ? 1.5 : 0.5} />); }
  for (let y = sY; y <= eY; y += spacing) { const ax = Math.abs(y) < 0.001; lines.push(<Line key={`gh${y}`} points={[sX * mm, y * mm, eX * mm, y * mm]} stroke={ax ? AXIS_COLOR : GRID_COLOR} strokeWidth={ax ? 1.5 : 0.5} />); }
  return <>{lines}</>;
};

const PadShape: React.FC<{ pad: FootprintPad; mm: number; selected?: boolean; onClick?: () => void }> = ({ pad, mm, selected, onClick }) => {
  const x = pad.x * mm, y = pad.y * mm, w = pad.width * mm, h = pad.height * mm;
  const cr = Math.min(w, h) * 0.25;
  const fill = selected ? PAD_SELECTED_COLOR : PAD_COLOR;
  const common = { onClick, opacity: 0.85 };
  return (
    <Group>
      {pad.shape === 'circle'
        ? <Circle x={x} y={y} radius={w / 2} fill={fill} {...common} />
        : <Rect x={x - w / 2} y={y - h / 2} width={w} height={h} fill={fill} cornerRadius={pad.shape === 'roundrect' ? cr : 0} {...common} />}
      {selected && <Rect x={x - w / 2 - 2} y={y - h / 2 - 2} width={w + 4} height={h + 4} stroke={SELECT_STROKE} strokeWidth={1.5} dash={[4, 2]} listening={false} />}
      <Text x={x - 4} y={y - 4} text={pad.id} fill={PIN1_COLOR} fontSize={Math.max(6, 0.3 * mm)} align="center" listening={false} />
    </Group>
  );
};

const LayerGeometry: React.FC<{ name: string; layer: FootprintLayer; mm: number; selectedIdx?: number; onClickItem?: (i: number) => void }> = ({ name, layer, mm, selectedIdx, onClickItem }) => {
  const color = LAYER_COLORS[name] || '#888';
  return (
    <Group>
      {layer.items?.map((item, i) => {
        const sel = selectedIdx === i;
        const strokeColor = sel ? SELECT_STROKE : color;
        if (item.type === 'rect' && item.x1 != null) {
          const rx = Math.min(item.x1, item.x2!) * mm, ry = Math.min(item.y1!, item.y2!) * mm;
          const rw = Math.abs(item.x2! - item.x1) * mm, rh = Math.abs(item.y2! - item.y1!) * mm;
          return <Rect key={i} x={rx} y={ry} width={rw} height={rh} stroke={strokeColor} strokeWidth={(item.width || 0.1) * mm} fill={item.fill ? color : undefined} opacity={item.fill ? 0.15 : 0.7} onClick={() => onClickItem?.(i)} />;
        }
        if (item.type === 'circle' && item.cx != null) return <Circle key={i} x={item.cx * mm} y={item.cy! * mm} radius={(item.r || 0) * mm} stroke={strokeColor} strokeWidth={(item.width || 0.1) * mm} fill={item.fill ? color : undefined} opacity={item.fill ? 0.15 : 0.7} onClick={() => onClickItem?.(i)} />;
        return null;
      })}
    </Group>
  );
};

const SilkscreenOutline: React.FC<{ silkscreen: Silkscreen; mm: number }> = ({ silkscreen, mm }) => {
  if (!silkscreen.outline?.length) return null;
  return <Line points={silkscreen.outline.flatMap(([x, y]) => [x * mm, y * mm])} closed stroke={LAYER_COLORS['F.SilkS']} strokeWidth={0.12 * mm} opacity={0.6} listening={false} />;
};

const Pin1Marker: React.FC<{ marker?: Silkscreen['pin1Marker']; mm: number }> = ({ marker, mm }) => {
  if (!marker) return null;
  return <Circle x={marker.x * mm} y={marker.y * mm} radius={0.15 * mm} fill={PIN1_COLOR} opacity={0.9} listening={false} />;
};

const DimensionAnnotations: React.FC<{ dims: Dimensions; bounds: ReturnType<typeof calcBounds>; mm: number }> = ({ dims, bounds, mm }) => {
  const items: React.ReactNode[] = [];
  const m = 0.8 * mm, fs = Math.max(8, 0.4 * mm);
  if (dims.bodyWidth) { const y = bounds.maxY * mm + m, x1 = -dims.bodyWidth / 2 * mm, x2 = dims.bodyWidth / 2 * mm; items.push(<Line key="dw" points={[x1, y, x2, y]} stroke={DIM_COLOR} strokeWidth={1} />, <Text key="dwt" x={(x1 + x2) / 2 - 15} y={y + 4} text={`${dims.bodyWidth}mm`} fill={DIM_COLOR} fontSize={fs} />); }
  if (dims.bodyHeight) { const x = bounds.maxX * mm + m, y1 = -dims.bodyHeight / 2 * mm, y2 = dims.bodyHeight / 2 * mm; items.push(<Line key="dh" points={[x, y1, x, y2]} stroke={DIM_COLOR} strokeWidth={1} />, <Text key="dht" x={x + 4} y={(y1 + y2) / 2 - 5} text={`${dims.bodyHeight}mm`} fill={DIM_COLOR} fontSize={fs} />); }
  return <>{items}</>;
};

const RulerOverlay: React.FC<{ points: { x: number; y: number }[]; mm: number }> = ({ points, mm }) => {
  if (points.length < 2) return null;
  const [p1, p2] = points;
  const dx = (p2.x - p1.x) / mm, dy = (p2.y - p1.y) / mm, dist = Math.sqrt(dx * dx + dy * dy);
  const mx = (p1.x + p2.x) / 2, my = (p1.y + p2.y) / 2, fs = Math.max(10, 0.5 * mm);
  return (<Group>
    <Line points={[p1.x, p1.y, p2.x, p2.y]} stroke={RULER_COLOR} strokeWidth={1.5} dash={[6, 3]} />
    <Circle x={p1.x} y={p1.y} radius={3} fill={RULER_COLOR} /><Circle x={p2.x} y={p2.y} radius={3} fill={RULER_COLOR} />
    <Rect x={mx - 25} y={my - fs - 4} width={50} height={fs + 6} fill="rgba(0,0,0,0.7)" cornerRadius={3} />
    <Text x={mx - 23} y={my - fs - 1} text={`${dist.toFixed(2)}mm`} fill={RULER_COLOR} fontSize={fs} />
  </Group>);
};

// ---------------------------------------------------------------------------
// Properties Panel (Phase 2 — edit selected element)
// ---------------------------------------------------------------------------
const PropertiesPanel: React.FC<{
  selection: SelectionId;
  footprint: FootprintData;
  dimensions?: Dimensions | null;
  editable?: boolean;
  onPadChange?: (idx: number, pad: FootprintPad) => void;
  onDimChange?: (dims: Dimensions) => void;
}> = ({ selection, footprint, dimensions, editable, onPadChange, onDimChange }) => {
  if (!selection) return null;

  // Parse selection ID
  const [type, ...rest] = selection.split(':');

  if (type === 'pad') {
    const idx = parseInt(rest[0], 10);
    const pad = footprint.pads?.[idx];
    if (!pad) return null;
    const update = (key: keyof FootprintPad, val: string) => {
      if (!editable || !onPadChange) return;
      const numVal = parseFloat(val);
      if (key === 'id' || key === 'shape') onPadChange(idx, { ...pad, [key]: val });
      else if (!isNaN(numVal)) onPadChange(idx, { ...pad, [key]: numVal });
    };
    return (
      <div className="px-3 py-2 bg-gray-800 border-t border-gray-600 text-[11px] text-gray-300 space-y-1">
        <div className="text-xs font-medium text-green-400 mb-1">Pad {pad.id}</div>
        <div className="grid grid-cols-4 gap-x-2 gap-y-1">
          <label>ID</label><input className="col-span-3 bg-gray-700 px-1 rounded text-gray-200" value={pad.id} onChange={e => update('id', e.target.value)} readOnly={!editable} />
          <label>Shape</label>
          <select className="col-span-3 bg-gray-700 px-1 rounded text-gray-200" value={pad.shape} onChange={e => update('shape', e.target.value)} disabled={!editable}>
            <option value="roundrect">roundrect</option><option value="rect">rect</option><option value="circle">circle</option><option value="oval">oval</option>
          </select>
          <label>X (mm)</label><input type="number" step="0.01" className="col-span-3 bg-gray-700 px-1 rounded text-gray-200" value={pad.x} onChange={e => update('x', e.target.value)} readOnly={!editable} />
          <label>Y (mm)</label><input type="number" step="0.01" className="col-span-3 bg-gray-700 px-1 rounded text-gray-200" value={pad.y} onChange={e => update('y', e.target.value)} readOnly={!editable} />
          <label>W (mm)</label><input type="number" step="0.01" className="col-span-3 bg-gray-700 px-1 rounded text-gray-200" value={pad.width} onChange={e => update('width', e.target.value)} readOnly={!editable} />
          <label>H (mm)</label><input type="number" step="0.01" className="col-span-3 bg-gray-700 px-1 rounded text-gray-200" value={pad.height} onChange={e => update('height', e.target.value)} readOnly={!editable} />
        </div>
      </div>
    );
  }

  if (type === 'dim' && dimensions) {
    const update = (key: keyof Dimensions, val: string) => {
      if (!editable || !onDimChange) return;
      const numVal = parseFloat(val);
      if (!isNaN(numVal)) onDimChange({ ...dimensions, [key]: numVal });
    };
    return (
      <div className="px-3 py-2 bg-gray-800 border-t border-gray-600 text-[11px] text-gray-300 space-y-1">
        <div className="text-xs font-medium text-cyan-400 mb-1">Dimensions</div>
        <div className="grid grid-cols-4 gap-x-2 gap-y-1">
          <label>Body W</label><input type="number" step="0.1" className="col-span-3 bg-gray-700 px-1 rounded text-gray-200" value={dimensions.bodyWidth ?? ''} onChange={e => update('bodyWidth', e.target.value)} readOnly={!editable} />
          <label>Body H</label><input type="number" step="0.1" className="col-span-3 bg-gray-700 px-1 rounded text-gray-200" value={dimensions.bodyHeight ?? ''} onChange={e => update('bodyHeight', e.target.value)} readOnly={!editable} />
          <label>Pad↔Pad</label><input type="number" step="0.1" className="col-span-3 bg-gray-700 px-1 rounded text-gray-200" value={dimensions.padToPad ?? ''} onChange={e => update('padToPad', e.target.value)} readOnly={!editable} />
        </div>
      </div>
    );
  }

  return null;
};

// ---------------------------------------------------------------------------
// Toolbar
// ---------------------------------------------------------------------------
const Toolbar: React.FC<{
  onFit: () => void; onZoomIn: () => void; onZoomOut: () => void;
  showGrid: boolean; onToggleGrid: () => void;
  rulerActive: boolean; onToggleRuler: () => void;
  showPng: boolean; hasPng: boolean; onTogglePng: () => void;
  pngOpacity: number; onPngOpacity: (v: number) => void;
}> = ({ onFit, onZoomIn, onZoomOut, showGrid, onToggleGrid, rulerActive, onToggleRuler, showPng, hasPng, onTogglePng, pngOpacity, onPngOpacity }) => {
  const btn = (active: boolean) => `p-1 rounded ${active ? 'text-blue-600 bg-blue-100 dark:bg-blue-900/40' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`;
  return (
    <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
      <button onClick={onFit} className={btn(false)} title="Fit to view"><Maximize2 className="w-3.5 h-3.5" /></button>
      <button onClick={onZoomIn} className={btn(false)} title="Zoom in"><ZoomIn className="w-3.5 h-3.5" /></button>
      <button onClick={onZoomOut} className={btn(false)} title="Zoom out"><ZoomOut className="w-3.5 h-3.5" /></button>
      <div className="w-px h-4 bg-gray-300 dark:bg-gray-500 mx-1" />
      <button onClick={onToggleGrid} className={btn(showGrid)} title="Toggle grid"><Grid3x3 className="w-3.5 h-3.5" /></button>
      <button onClick={onToggleRuler} className={btn(rulerActive)} title="Ruler tool"><Ruler className="w-3.5 h-3.5" /></button>
      {hasPng && <>
        <div className="w-px h-4 bg-gray-300 dark:bg-gray-500 mx-1" />
        <button onClick={onTogglePng} className={btn(showPng)} title="Toggle reference image"><ImageIcon className="w-3.5 h-3.5" /></button>
        {showPng && <input type="range" min={5} max={100} value={pngOpacity} onChange={e => onPngOpacity(Number(e.target.value))} className="w-16 h-3 accent-blue-500" title={`Opacity ${pngOpacity}%`} />}
      </>}
      <div className="flex-1" />
      {rulerActive && <span className="text-[10px] text-orange-400">Click two points to measure</span>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab bar
// ---------------------------------------------------------------------------
const TabBar: React.FC<{ tab: ViewTab; onChange: (t: ViewTab) => void; layers?: Record<string, FootprintLayer>; vis: Record<string, boolean>; onToggle: (n: string) => void }> = ({ tab, onChange, layers, vis, onToggle }) => {
  const ok = (n: string) => vis[n] !== false;
  return (
    <div className="flex items-center bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-600 text-[10px]">
      <button onClick={() => onChange('layout')} className={`flex items-center gap-1 px-3 py-1.5 border-b-2 ${tab === 'layout' ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700'}`}><Eye className="w-3 h-3" /> Layout</button>
      <button onClick={() => onChange('json')} className={`flex items-center gap-1 px-3 py-1.5 border-b-2 ${tab === 'json' ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700'}`}><Code2 className="w-3 h-3" /> JSON</button>
      {tab === 'layout' && layers && (
        <div className="flex flex-wrap gap-1.5 ml-auto px-2">
          {Object.keys(layers).map(n => <button key={n} onClick={() => onToggle(n)} className={`flex items-center gap-0.5 px-1 py-0.5 rounded ${ok(n) ? 'opacity-100' : 'opacity-40'}`}><span className="w-2 h-2 rounded-sm" style={{ backgroundColor: LAYER_COLORS[n] || '#888' }} /><span className="text-gray-600 dark:text-gray-300">{n}</span></button>)}
          <span className="flex items-center gap-0.5 px-1 py-0.5"><span className="w-2 h-2 rounded-sm" style={{ backgroundColor: PAD_COLOR }} /><span className="text-gray-600 dark:text-gray-300">Pads</span></span>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const FootprintPreview: React.FC<FootprintPreviewProps> = ({ footprint, dimensions: initDims, croppedPngBase64, rawJson, height = 320, editable, onFootprintChange }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<any>(null);
  const [containerWidth, setContainerWidth] = useState(400);
  const [showGrid, setShowGrid] = useState(true);
  const [layerVis, setLayerVis] = useState<Record<string, boolean>>({});
  const [viewTab, setViewTab] = useState<ViewTab>('layout');
  const [rulerActive, setRulerActive] = useState(false);
  const [rulerPoints, setRulerPoints] = useState<{ x: number; y: number }[]>([]);
  const [selection, setSelection] = useState<SelectionId>(null);
  const [showPng, setShowPng] = useState(true);
  const [pngOpacity, setPngOpacity] = useState(35);

  // Editable copies
  const [fpData, setFpData] = useState<FootprintData | null>(footprint);
  const [dims, setDims] = useState<Dimensions | null>(initDims || null);
  useEffect(() => { setFpData(footprint); }, [footprint]);
  useEffect(() => { setDims(initDims || null); }, [initDims]);

  // Load cropped PNG as HTMLImageElement
  const pngImage = useLoadImage(croppedPngBase64);

  // Responsive width
  useEffect(() => {
    const el = containerRef.current; if (!el) return;
    const ro = new ResizeObserver(entries => { for (const e of entries) setContainerWidth(e.contentRect.width); });
    ro.observe(el); return () => ro.disconnect();
  }, []);

  const bounds = useMemo(() => fpData ? calcBounds(fpData) : { minX: -5, minY: -5, maxX: 5, maxY: 5 }, [fpData]);
  const bestFit = useMemo(() => {
    const gW = bounds.maxX - bounds.minX, gH = bounds.maxY - bounds.minY, pad = 2;
    const s = Math.min(containerWidth / (gW + pad * 2), height / (gH + pad * 2));
    return { scale: s, offsetX: containerWidth / 2 - ((bounds.minX + bounds.maxX) / 2) * s, offsetY: height / 2 - ((bounds.minY + bounds.maxY) / 2) * s };
  }, [bounds, containerWidth, height]);

  const [stagePos, setStagePos] = useState({ x: 0, y: 0, scale: 1 });
  const fitToView = useCallback(() => { setStagePos({ x: bestFit.offsetX, y: bestFit.offsetY, scale: 1 }); }, [bestFit]);
  useEffect(() => { fitToView(); }, [fitToView]);

  const handleWheel = useCallback((e: any) => {
    e.evt.preventDefault(); const stage = stageRef.current; if (!stage) return;
    const ptr = stage.getPointerPosition(); if (!ptr) return;
    const old = stagePos.scale, mp = { x: (ptr.x - stagePos.x) / old, y: (ptr.y - stagePos.y) / old };
    const dir = e.evt.deltaY > 0 ? -1 : 1, ns = Math.max(0.1, Math.min(20, dir > 0 ? old * 1.15 : old / 1.15));
    setStagePos({ scale: ns, x: ptr.x - mp.x * ns, y: ptr.y - mp.y * ns });
  }, [stagePos]);

  const handleStageClick = useCallback((e: any) => {
    if (rulerActive) {
      const stage = stageRef.current; if (!stage) return;
      const ptr = stage.getPointerPosition(); if (!ptr) return;
      const x = (ptr.x - stagePos.x) / stagePos.scale, y = (ptr.y - stagePos.y) / stagePos.scale;
      setRulerPoints(prev => prev.length >= 2 ? [{ x, y }] : [...prev, { x, y }]);
      return;
    }
    // Deselect when clicking empty space
    if (e.target === e.target.getStage() || e.target.attrs?.fill === PCB_BG) {
      setSelection(null);
    }
  }, [rulerActive, stagePos]);

  // Edit handlers
  const handlePadChange = useCallback((idx: number, pad: FootprintPad) => {
    if (!fpData?.pads) return;
    const pads = [...fpData.pads]; pads[idx] = pad;
    const updated = { ...fpData, pads };
    setFpData(updated);
    onFootprintChange?.(updated);
  }, [fpData, onFootprintChange]);

  const handleDimChange = useCallback((d: Dimensions) => {
    setDims(d);
  }, []);

  const mm = bestFit.scale;
  const isVis = (n: string) => layerVis[n] !== false;

  if (!fpData) return <div className="flex items-center justify-center h-40 text-sm text-gray-400">No footprint data</div>;

  // PNG alignment: center the image at (0,0), scale so body width maps correctly
  const pngProps = useMemo(() => {
    if (!pngImage || !dims?.bodyWidth) return null;
    // Assume body occupies ~60% of cropped image width (tunable)
    const bodyRatio = 0.6;
    const imgWInMm = dims.bodyWidth / bodyRatio;
    const imgHInMm = imgWInMm * (pngImage.height / pngImage.width);
    const imgW = imgWInMm * mm, imgH = imgHInMm * mm;
    return { x: -imgW / 2, y: -imgH / 2, width: imgW, height: imgH };
  }, [pngImage, dims, mm]);

  return (
    <div ref={containerRef} className="w-full">
      {viewTab === 'layout' && (
        <>
          <Toolbar
            onFit={fitToView} onZoomIn={() => setStagePos(p => ({ ...p, scale: Math.min(20, p.scale * 1.3) }))} onZoomOut={() => setStagePos(p => ({ ...p, scale: Math.max(0.1, p.scale / 1.3) }))}
            showGrid={showGrid} onToggleGrid={() => setShowGrid(g => !g)}
            rulerActive={rulerActive} onToggleRuler={() => { setRulerActive(a => !a); setRulerPoints([]); }}
            showPng={showPng} hasPng={!!pngImage} onTogglePng={() => setShowPng(v => !v)} pngOpacity={pngOpacity} onPngOpacity={setPngOpacity}
          />
          <Stage ref={stageRef} width={containerWidth} height={height} x={stagePos.x} y={stagePos.y} scaleX={stagePos.scale} scaleY={stagePos.scale}
            draggable={!rulerActive} onDragEnd={(e: any) => setStagePos(p => ({ ...p, x: e.target.x(), y: e.target.y() }))} onWheel={handleWheel} onClick={handleStageClick}
            style={{ cursor: rulerActive ? 'crosshair' : 'grab' }}
          >
            {/* BG */}
            <Layer listening={false}><Rect x={(-stagePos.x / stagePos.scale) - 2000} y={(-stagePos.y / stagePos.scale) - 2000} width={4000 + containerWidth / stagePos.scale} height={4000 + height / stagePos.scale} fill={PCB_BG} /></Layer>
            {/* Grid */}
            {showGrid && <Layer listening={false}><GridLayer stagePos={stagePos} w={containerWidth} h={height} mm={mm} spacing={0.5} /></Layer>}
            {/* Reference PNG background */}
            {showPng && pngImage && pngProps && (
              <Layer listening={false}><KonvaImage image={pngImage} x={pngProps.x} y={pngProps.y} width={pngProps.width} height={pngProps.height} opacity={pngOpacity / 100} /></Layer>
            )}
            {/* Layers */}
            <Layer>{fpData.layers && Object.entries(fpData.layers).map(([n, layer]) => isVis(n) ? <LayerGeometry key={n} name={n} layer={layer} mm={mm} selectedIdx={selection?.startsWith(`layer:${n}:`) ? parseInt(selection.split(':')[2], 10) : undefined} onClickItem={(i) => setSelection(`layer:${n}:${i}`)} /> : null)}</Layer>
            {/* Silkscreen */}
            <Layer listening={false}>{fpData.silkscreen && <><SilkscreenOutline silkscreen={fpData.silkscreen} mm={mm} /><Pin1Marker marker={fpData.silkscreen.pin1Marker} mm={mm} /></>}</Layer>
            {/* Pads */}
            <Layer>{fpData.pads?.map((pad, i) => <PadShape key={pad.id} pad={pad} mm={mm} selected={selection === `pad:${i}`} onClick={() => setSelection(`pad:${i}`)} />)}</Layer>
            {/* Dims */}
            {dims && <Layer listening={false}><DimensionAnnotations dims={dims} bounds={bounds} mm={mm} /></Layer>}
            {/* Ruler */}
            {rulerPoints.length > 0 && <Layer listening={false}><RulerOverlay points={rulerPoints} mm={mm} /></Layer>}
          </Stage>
          {/* Properties panel (Phase 2) */}
          <PropertiesPanel selection={selection} footprint={fpData} dimensions={dims} editable={editable} onPadChange={handlePadChange} onDimChange={handleDimChange} />
        </>
      )}
      {viewTab === 'json' && <pre className="px-4 py-3 text-xs text-gray-300 bg-gray-900 overflow-auto font-mono" style={{ height, maxHeight: height }}>{JSON.stringify(rawJson || { footprint: fpData, dimensions: dims }, null, 2)}</pre>}
      <TabBar tab={viewTab} onChange={setViewTab} layers={fpData.layers} vis={layerVis} onToggle={n => setLayerVis(v => ({ ...v, [n]: !(v[n] !== false) }))} />
    </div>
  );
};

export default FootprintPreview;
